"use client"

import { useState } from "react"
import { Button } from "./Button"
import { cn } from "@/lib/utils"
import { ThumbsUp, ThumbsDown, Copy, CheckCheck, ExternalLink } from "lucide-react"
import { MessageAction, NavigateAction, RatingAction, CopyAction } from "./chat-panel-types"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

interface MessageActionsProps {
  actions: MessageAction[]
  messageRole: "user" | "assistant"
  className?: string
}

export function MessageActions({ actions, messageRole, className }: MessageActionsProps) {
  const navigate = useNavigate()
  const [ratings, setRatings] = useState<Record<string, "positive" | "negative">>({})
  const [copied, setCopied] = useState<Record<string, boolean>>({})

  const handleAction = async (action: MessageAction) => {
    switch (action.type) {
      case "navigate": {
        const navAction = action as NavigateAction
        if (navAction.data.route) {
          navigate(navAction.data.route)
          toast.success("Navigating", {
            description: `Taking you to ${navAction.label}`,
            duration: 2000,
          })
        }
        break
      }

      case "highlight": {
        // TODO: Implement highlight functionality
        toast.info("Highlight", {
          description: "Highlighting feature coming soon",
          duration: 2000,
        })
        break
      }

      case "rating": {
        const ratingAction = action as RatingAction
        // Toggle rating
        const currentRating = ratings[ratingAction.data.messageId]
        const newRating = ratingAction.data.value

        if (currentRating === newRating) {
          // Remove rating if clicking same button
          const newRatings = { ...ratings }
          delete newRatings[ratingAction.data.messageId]
          setRatings(newRatings)
        } else {
          // Set new rating
          setRatings({
            ...ratings,
            [ratingAction.data.messageId]: newRating!,
          })
          toast.success(
            newRating === "positive" ? "Thanks for the feedback!" : "Thanks, we'll improve",
            {
              duration: 2000,
            }
          )
        }
        break
      }

      case "copy": {
        const copyAction = action as CopyAction
        try {
          await navigator.clipboard.writeText(copyAction.data.text)
          setCopied({ ...copied, [action.id]: true })
          toast.success("Copied to clipboard", { duration: 2000 })
          setTimeout(() => {
            setCopied(prev => {
              const newCopied = { ...prev }
              delete newCopied[action.id]
              return newCopied
            })
          }, 2000)
        } catch (err) {
          toast.error("Failed to copy", { duration: 2000 })
        }
        break
      }

      case "custom": {
        if (action.onAction) {
          await action.onAction(action)
        }
        break
      }
    }

    // Call custom handler if provided
    if (action.onAction && action.type !== "custom") {
      await action.onAction(action)
    }
  }

  const renderActionButton = (action: MessageAction) => {
    const Icon = action.icon

    // Special rendering for rating actions
    if (action.type === "rating") {
      const ratingAction = action as RatingAction
      const currentRating = ratings[ratingAction.data.messageId]

      return (
        <div key={action.id} className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 px-2 text-xs gap-1.5",
              currentRating === "positive" && "bg-green-500/10 text-green-600 hover:bg-green-500/20"
            )}
            onClick={() =>
              handleAction({
                ...action,
                data: { ...ratingAction.data, value: "positive" },
              } as RatingAction)
            }
          >
            <ThumbsUp className="size-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 px-2 text-xs gap-1.5",
              currentRating === "negative" && "bg-red-500/10 text-red-600 hover:bg-red-500/20"
            )}
            onClick={() =>
              handleAction({
                ...action,
                data: { ...ratingAction.data, value: "negative" },
              } as RatingAction)
            }
          >
            <ThumbsDown className="size-3" />
          </Button>
        </div>
      )
    }

    // Special rendering for copy actions
    if (action.type === "copy") {
      const isCopied = copied[action.id]
      return (
        <Button
          key={action.id}
          variant={action.variant || "ghost"}
          size="sm"
          className="h-7 px-2 text-xs gap-1.5"
          onClick={() => handleAction(action)}
        >
          {isCopied ? <CheckCheck className="size-3" /> : <Copy className="size-3" />}
          {isCopied ? "Copied" : action.label}
        </Button>
      )
    }

    // Default button rendering
    return (
      <Button
        key={action.id}
        variant={action.variant || "secondary"}
        size="sm"
        className="h-7 px-3 text-xs gap-1.5"
        onClick={() => handleAction(action)}
      >
        {Icon && <Icon className="size-3" />}
        {action.label}
        {action.type === "navigate" && <ExternalLink className="size-3 opacity-50" />}
      </Button>
    )
  }

  if (actions.length === 0) return null

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5 mt-2", className)}>
      {actions.map(renderActionButton)}
    </div>
  )
}
